import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Target, Heart, Globe, ArrowRight } from "lucide-react";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

export default function AboutPage() {
  const values = [
    {
      icon: Heart,
      title: "Passion for Indonesia",
      description: "We live and breathe Indonesia. Our deep love for this nation drives us to share its wonders with the world."
    },
    {
      icon: Users,
      title: "Community First",
      description: "We work closely with local communities, ensuring tourism benefits everyone and preserves cultural heritage."
    },
    {
      icon: Target,
      title: "Excellence",
      description: "From planning to execution, we maintain the highest standards in every aspect of our service."
    },
    {
      icon: Globe,
      title: "Sustainability",
      description: "We are committed to responsible tourism that protects Indonesia's natural beauty for future generations."
    }
  ];

  const team = [
    {
      name: "Satria",
      role: "CEO",
      image: "https://customer-assets.emergentagent.com/job_build-sites-32/artifacts/d65v0ki5_freepik__close-up-and-adjust-the-character-wearing-dark-blu__90953.png"
    },
    {
      name: "Tania",
      role: "Marketing",
      image: "https://customer-assets.emergentagent.com/job_build-sites-32/artifacts/r4bdsokj_freepik__lucylie-a-cinematic-closeup-portrait-of-an-elegant__14852.jpeg"
    },
    {
      name: "Kristopher",
      role: "Admin",
      image: "https://customer-assets.emergentagent.com/job_build-sites-32/artifacts/ovc8rdtl_freepik__create-a-brand-new-character-close-up-image-chines__90954.png"
    }
  ];

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/5234072/pexels-photo-5234072.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Lake Toba"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0E2954]/90 to-[#0E2954]/60" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="label-style text-white/70 mb-4">
              About Us
            </motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-6xl text-white max-w-2xl">
              Your Gateway to Indonesia
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-16 items-center"
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-3xl md:text-4xl mb-6">Our Story</h2>
              <div className="space-y-4 text-[#666666] leading-relaxed">
                <p>
                  Founded with a passion for sharing Indonesia's extraordinary beauty with the world, 
                  IndonesiaTourism has grown from a small local operation to a comprehensive tourism 
                  and business consulting service.
                </p>
                <p>
                  We believe that travel should be transformative. Beyond just visiting places, 
                  we create meaningful connections between our guests and the rich tapestry of 
                  Indonesian culture, nature, and people.
                </p>
                <p>
                  For business partners, we offer unparalleled expertise in navigating Indonesia's 
                  tourism industry. From establishing resorts to launching tour operations, 
                  we've helped countless entrepreneurs turn their Indonesian dreams into reality.
                </p>
              </div>
            </motion.div>
            <motion.div variants={fadeInUp}>
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://images.pexels.com/photos/2583852/pexels-photo-2583852.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Raja Ampat"
                  className="w-full aspect-[3/4] object-cover rounded-lg"
                />
                <img 
                  src="https://images.unsplash.com/photo-1584022649364-e5f929f6d9ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxrb21vZG8lMjBkcmFnb24lMjBpbmRvbmVzaWF8ZW58MHx8fHwxNzc0MzMxNDk1fDA&ixlib=rb-4.1.0&q=85"
                  alt="Komodo"
                  className="w-full aspect-[3/4] object-cover rounded-lg mt-8"
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl mb-12 text-center">
              Our Values
            </motion.h2>
            <motion.div variants={fadeInUp} className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div 
                  key={index}
                  data-testid={`value-card-${index}`}
                  className="p-8 border border-[#E5E5E5] rounded-lg hover:border-[#0E2954] transition-colors"
                >
                  <div className="w-12 h-12 bg-[#1B4D3E]/10 rounded-full flex items-center justify-center mb-6">
                    <value.icon className="w-6 h-6 text-[#1B4D3E]" />
                  </div>
                  <h3 className="text-xl mb-3">{value.title}</h3>
                  <p className="text-[#666666] text-sm">{value.description}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Team */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl mb-4 text-center">
              Meet Our Team
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-[#666666] text-center max-w-2xl mx-auto mb-12">
              Our experienced team combines local expertise with international standards 
              to deliver exceptional experiences.
            </motion.p>
            <motion.div variants={fadeInUp} className="grid md:grid-cols-3 gap-8">
              {team.map((member, index) => (
                <div 
                  key={index}
                  data-testid={`team-member-${index}`}
                  className="text-center"
                >
                  <div className="mb-6 overflow-hidden rounded-lg">
                    <img 
                      src={member.image}
                      alt={member.name}
                      className="w-full aspect-[4/5] object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <h3 className="text-xl">{member.name}</h3>
                  <p className="text-[#666666]">{member.role}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#0E2954]">
        <div className="container-custom text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl text-white mb-6">
              Ready to Experience Indonesia?
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto mb-8">
              Whether you're planning a dream vacation or exploring business opportunities, 
              we're here to guide you every step of the way.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/membership"
                data-testid="about-cta-membership-btn"
                className="btn-primary"
              >
                Become a Member
              </Link>
              <Link 
                to="/destinations"
                data-testid="about-cta-destinations-btn"
                className="text-white font-medium flex items-center gap-2"
              >
                Explore Destinations
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
